import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import * as maptilersdk from '@maptiler/sdk'
import ViewshedOverlay from './ViewshedOverlay'
import { viewshedStore } from './viewshed-store'
import { terrainStore } from '../terrain-analysis/terrain-store'
import { activeFeatureStore } from '../terrain-analysis/active-feature-store'
import { fetchElevTile, elevCache } from '../terrain-analysis/terrain-cache'
import {
  MILES_TO_METERS, EYE_HEIGHT_M, VIEWSHED_ZOOM,
  lngLatToTilePx, metersPerPixelAt, tilesInBbox, viewshedBbox, stitchElevGrid, gridBounds,
} from './viewshed-utils'
import './viewshed.scss'

// ─── Design tokens (mirror terrain-analysis) ────────────────────────────────
const T = {
  green:      '#589500',
  greenDim:   '#589500',
  greenGlow:  'rgba(88,149,0,0.4)',
  black:      '#000',
  panelBg:    'rgba(8,9,7,0.92)',
  panelBorder:'rgba(255,255,255,0.08)',
  divider:    'rgba(255,255,255,0.06)',
  textPri:    '#FFFFFF',
  textSec:    'rgba(255,255,255,0.55)',
  textTer:    'rgba(255,255,255,0.4)',
  chip:       'rgba(255,255,255,0.05)',
  chipBorder: 'rgba(255,255,255,0.1)',
}

// ─── Icons ───────────────────────────────────────────────────────────────────
const BinocularsIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 10V8a2 2 0 012-2h2a2 2 0 012 2v2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M15 10V8a2 2 0 012-2h2a2 2 0 012 2v2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="7"  cy="15" r="4" stroke={color} strokeWidth="1.8"/>
    <circle cx="17" cy="15" r="4" stroke={color} strokeWidth="1.8"/>
    <path d="M11 15h2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
)

const ChevronIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// ─── Reusable sub-components ─────────────────────────────────────────────────
function Section({ label, hint, children }) {
  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: T.textPri, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 4, height: 4, borderRadius: 2, background: T.green }} />
          {label}
        </div>
        {hint && <div style={{ fontSize: 10, color: T.textTer }}>{hint}</div>}
      </div>
      {children}
    </div>
  )
}

function Toggle({ value, onChange }) {
  return (
    <button onClick={onChange} style={{
      width: 36, height: 20, borderRadius: 10, border: `1px solid ${value ? T.greenDim : T.chipBorder}`,
      background: value ? T.green : T.chip, position: 'relative', cursor: 'pointer', padding: 0,
      transition: 'all 160ms ease', flexShrink: 0,
    }}>
      <div style={{ position: 'absolute', top: 1, left: value ? 17 : 1, width: 16, height: 16, borderRadius: 8, background: value ? T.black : T.textPri, transition: 'left 160ms ease' }} />
    </button>
  )
}

function OpacitySlider({ value, onChange }) {
  const trackRef = useRef(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!dragging) return
    const onMove = e => {
      const rect = trackRef.current.getBoundingClientRect()
      onChange(Math.round(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * 100))
    }
    const onUp = () => setDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [dragging, onChange])

  return (
    <div ref={trackRef} style={{ position: 'relative', height: 20, cursor: 'pointer', userSelect: 'none' }}
      onMouseDown={e => {
        setDragging(true)
        const rect = trackRef.current.getBoundingClientRect()
        onChange(Math.round(((e.clientX - rect.left) / rect.width) * 100))
      }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 8, height: 4, background: `repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 3px, rgba(255,255,255,0.02) 3px 6px)`, borderRadius: 2 }} />
      <div style={{ position: 'absolute', left: 0, top: 8, height: 4, width: `${value}%`, background: T.green, borderRadius: 2 }} />
      <div style={{ position: 'absolute', left: `${value}%`, top: 10, transform: 'translate(-50%, -50%)', width: 14, height: 14, borderRadius: 7, background: T.green, border: `2px solid ${T.black}`, boxShadow: `0 0 0 1px ${T.greenDim}, 0 0 8px ${T.greenGlow}`, cursor: 'grab' }} />
    </div>
  )
}

const RADII = [
  { label: '½ mi', value: 0.5 },
  { label: '1 mi', value: 1 },
  { label: '2 mi', value: 2 },
]

// ─── Marker DOM element factory ───────────────────────────────────────────────
function createMarkerEl(computing = false) {
  const el = document.createElement('div')
  el.className = `viewshed-marker${computing ? ' computing' : ''}`
  el.innerHTML = `
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M3 10V8a2 2 0 012-2h2a2 2 0 012 2v2" stroke="#589500" stroke-width="1.8" stroke-linecap="round"/>
      <path d="M15 10V8a2 2 0 012-2h2a2 2 0 012 2v2" stroke="#589500" stroke-width="1.8" stroke-linecap="round"/>
      <circle cx="7"  cy="15" r="4" stroke="#589500" stroke-width="1.8"/>
      <circle cx="17" cy="15" r="4" stroke="#589500" stroke-width="1.8"/>
      <path d="M11 15h2" stroke="#589500" stroke-width="1.8" stroke-linecap="round"/>
    </svg>
  `
  return el
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function Viewshed({ map }) {
  const [open,        setOpen]        = useState(false)
  const [enabled,     setEnabled]     = useState(false)
  const [collapsed,   setCollapsed]   = useState(false)
  const [radiusMi,    setRadiusMi]    = useState(1)
  const [opacity,     setOpacity]     = useState(70)
  const [showHidden,  setShowHidden]  = useState(true)
  const [observer,    setObserver]    = useState(null)   // { lng, lat }
  const [status,      setStatus]      = useState('idle') // 'idle'|'computing'|'ready'|'error'
  const [vsResult,    setVsResult]    = useState(null)
  const [terrainActive, setTerrainActive] = useState(terrainStore.active)

  const workerRef    = useRef(null)
  const markerRef    = useRef(null)
  const opacityRef   = useRef(opacity)
  useEffect(() => { opacityRef.current = opacity }, [opacity])
  const radiusRef    = useRef(radiusMi)
  useEffect(() => { radiusRef.current = radiusMi }, [radiusMi])

  // Track terrain-analysis active state so we know which overlay mode to use
  useEffect(() => terrainStore.onChange(() => setTerrainActive(terrainStore.active)), [])

  // Always expand when opening
  useEffect(() => { if (open) setCollapsed(false) }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close when terrain (or another feature) takes over
  useEffect(() => activeFeatureStore.onChange(() => {
    if (activeFeatureStore.active !== 'viewshed') {
      setOpen(false)
      setEnabled(false)
    }
  }), [])

  // ── Viewshed computation ───────────────────────────────────────────────────
  const runViewshed = useCallback(async (lng, lat, radMi) => {
    setStatus('computing')
    if (markerRef.current) {
      const el = markerRef.current.getElement()
      el.classList.add('computing')
    }
    try {
      const radiusM = radMi * MILES_TO_METERS
      const bbox    = viewshedBbox(lng, lat, radiusM)
      const tiles   = tilesInBbox(bbox.west, bbox.south, bbox.east, bbox.north, VIEWSHED_ZOOM)

      await Promise.all(tiles.map(({ tx, ty }) => fetchElevTile(VIEWSHED_ZOOM, tx, ty)))

      const { grid, width, height, minTx, minTy } = stitchElevGrid(elevCache, tiles)

      const { tx: obsTx, ty: obsTy, px: obsPx, py: obsPy } = lngLatToTilePx(lng, lat, VIEWSHED_ZOOM)
      const obsGridX = (obsTx - minTx) * 256 + obsPx
      const obsGridY = (obsTy - minTy) * 256 + obsPy
      const obsElevM = grid[obsGridY * width + obsGridX]
      const mpp      = metersPerPixelAt(lat, VIEWSHED_ZOOM)
      const maxDistPx = Math.round(radiusM / mpp)

      if (workerRef.current) workerRef.current.terminate()
      const worker = new Worker(new URL('./viewshed-worker.js', import.meta.url))
      workerRef.current = worker

      worker.onmessage = (e) => {
        const { visible } = e.data
        // The grid spans whole tiles, so it is placed by the tiles' bounds, not the smaller bbox
        // around the observer (which stretched the result off the observer, WEB-7101).
        const bounds = gridBounds(minTx, minTy, width, height, VIEWSHED_ZOOM)
        const result = { visible, gridWidth: width, gridHeight: height, bounds, obsGridX, obsGridY, maxDistPx }
        setVsResult(result)
        viewshedStore.set({ ...result, opacity: Math.round(opacityRef.current / 100 * 255) })
        setStatus('ready')
        if (markerRef.current) markerRef.current.getElement().classList.remove('computing')
      }
      worker.onerror = () => {
        setStatus('error')
        if (markerRef.current) markerRef.current.getElement().classList.remove('computing')
      }

      worker.postMessage(
        { elevGrid: grid, gridWidth: width, gridHeight: height, obsGridX, obsGridY, obsElevM, eyeHeightM: EYE_HEIGHT_M, mpp, maxDistPx },
        [grid.buffer],
      )
    } catch (err) {
      console.error('viewshed:', err)
      setStatus('error')
      if (markerRef.current) markerRef.current.getElement().classList.remove('computing')
    }
  }, [])

  // Re-sync store opacity when slider changes
  useEffect(() => {
    if (!vsResult) return
    viewshedStore.set({ ...vsResult, opacity: Math.round(opacity / 100 * 255) })
  }, [opacity, vsResult])

  // Re-run when radius changes (if observer already placed)
  useEffect(() => {
    if (!observer) return
    runViewshed(observer.lng, observer.lat, radiusMi)
  }, [radiusMi]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Marker lifecycle ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!map) return

    if (!enabled) {
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null }
      if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null }
      viewshedStore.clear()
      setVsResult(null)
      setObserver(null)
      setStatus('idle')
      return
    }

    // Place binoculars at map center on first enable
    const center   = map.getCenter()
    const lng      = center.lng
    const lat      = center.lat

    const el = createMarkerEl(true)
    const marker = new maptilersdk.Marker({ element: el, draggable: true, anchor: 'center' })
    marker.setLngLat([lng, lat]).addTo(map)
    markerRef.current = marker

    marker.on('dragend', () => {
      const pos = marker.getLngLat()
      setObserver({ lng: pos.lng, lat: pos.lat })
      runViewshed(pos.lng, pos.lat, radiusRef.current)
    })

    setObserver({ lng, lat })
    runViewshed(lng, lat, radiusMi)

    return () => {
      marker.remove()
      markerRef.current = null
    }
  }, [map, enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup worker on unmount
  useEffect(() => () => {
    if (workerRef.current) workerRef.current.terminate()
    viewshedStore.clear()
  }, [])

  // ── Derived overlay prop ──────────────────────────────────────────────────
  // Independent mode: show canvas when enabled + ready + terrain NOT active.
  // Combined mode: TerrainOverlay handles rendering via viewshedStore; canvas hidden.
  const independentViewshed = useMemo(() => {
    if (!enabled || !vsResult || terrainActive) return null
    return { ...vsResult, opacity, showHidden }
  }, [enabled, vsResult, opacity, showHidden, terrainActive])

  const fabActive = open
  const fabColor  = fabActive ? '#FFFFFF' : '#6E6E6E'

  return (
    <>
      <ViewshedOverlay map={map} viewshed={independentViewshed} />

      {/* FAB */}
      <button
        className={`viewshed-fab${fabActive ? ' active' : ''}`}
        onClick={() => { const next = !open; setOpen(next); setEnabled(next); activeFeatureStore.set(next ? 'viewshed' : null) }}
        title="Viewshed"
      >
        <BinocularsIcon size={15} color={fabColor} />
        <span style={{ color: fabColor }}>VIEWSHED</span>
      </button>

      {/* Panel */}
      <div
        className="viewshed-panel"
        style={{
          transform: open ? 'scale(1) translateY(0)' : 'scale(0.93) translateY(-8px)',
          opacity:   open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: `1px solid ${T.divider}`, background: 'linear-gradient(180deg, rgba(255,255,255,0.02), transparent)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: T.green, border: `1px solid ${T.greenDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BinocularsIcon size={15} color={T.black} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Viewshed</div>
            <div style={{ fontSize: 10, color: T.textSec, marginTop: 2 }}>
              {status === 'computing' ? 'Computing…'
                : status === 'ready' && observer ? `${observer.lat.toFixed(4)}°, ${observer.lng.toFixed(4)}°`
                : status === 'error' ? 'Error — try again'
                : '—'}
            </div>
            <div style={{ fontSize: 10, color: T.textSec, marginTop: 1 }}>
              {`Radius: ${radiusMi === 0.5 ? '½' : radiusMi} mile${radiusMi !== 1 ? 's' : ''}`}
            </div>
          </div>
          <button onClick={() => { setOpen(false); setEnabled(false); setCollapsed(false); activeFeatureStore.set(null) }} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.chipBorder}`, background: T.chip, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
            </svg>
          </button>
          <button onClick={() => setCollapsed(c => !c)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.chipBorder}`, background: T.chip, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }} title={collapsed ? 'Expand' : 'Collapse'}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {collapsed ? <polyline points="4,9 12,17 20,9" /> : <polyline points="4,15 12,7 20,15" />}
            </svg>
          </button>
        </div>

        {/* Body */}
        {!collapsed && <div style={{ overflowY: 'auto' }}>
          <Section label="Radius" hint={`${radiusMi === 0.5 ? '½' : radiusMi} mi`}>
            <div style={{ display: 'flex', background: T.chip, border: `1px solid ${T.chipBorder}`, borderRadius: 6, padding: 2 }}>
              {RADII.map(({ label, value }) => (
                <button key={value} onClick={() => setRadiusMi(value)} style={{
                  flex: 1, padding: '7px 4px', borderRadius: 4, border: 'none',
                  background: radiusMi === value ? T.green : 'transparent',
                  color: radiusMi === value ? T.black : T.textSec,
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  letterSpacing: '0.04em', transition: 'all 120ms ease',
                }}>{label}</button>
              ))}
            </div>
          </Section>

          <div style={{ height: 1, background: T.divider }} />

          <Section label="Show hidden">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 11, color: T.textSec }}>
                {showHidden ? 'Hidden areas shown in black' : 'Hidden areas transparent'}
              </div>
              <Toggle value={showHidden} onChange={() => setShowHidden(s => !s)} />
            </div>
          </Section>

          <div style={{ height: 1, background: T.divider }} />

          <Section label="Opacity" hint={`${opacity}%`}>
            <OpacitySlider value={opacity} onChange={setOpacity} />
          </Section>

          {status === 'ready' && observer && (
            <>
              <div style={{ height: 1, background: T.divider }} />
              <div style={{ padding: '10px 16px', fontSize: 10, color: T.textTer }}>
                Drag the binoculars on the map to move the observer.
              </div>
            </>
          )}
        </div>}
      </div>
    </>
  )
}
